import {
  Alert,
  Box,
  Button,
  Snackbar,
  Stack,
  Switch,
  ToggleButton,
  Typography,
  TextField,
  Collapse,
  IconButton,
  CircularProgress,
  Chip,
} from '@mui/material';
import React, {useEffect, useState} from 'react';
import AimdFlowQuestionBase from '../../components/aimdFlowQuestionBase';
import mainLogo from '../../images/logo_black_letters_2.png';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import {useLocation, useNavigate} from 'react-router-dom';
import {useCheckoutPaymentsApi} from '../../api/payment';
import {AnimationLoadingScreenWithProgressBar} from '../../components/loading/loadingScreen';
import {useUserApi} from '../../api/user';

const pricingScreendata = [
  {
    heading: 'Yearly Plan',
    subheading: '12 mo • USD$165.99',
    // amount: "$6.67/wk",
    amount: '$3.22/wk',
    isFreeTrialAvailable: '14-DAY FREE TRIAL',
    periodToSendInAPI: 'yearly',
  },
  {
    heading: 'Monthly Plan',
    subheading: '1 mo •USD$15.99',
    // amount: "$6.67/wk",
    amount: ' $3.73/wk',
    isFreeTrialAvailable: '14-DAY FREE TRIAL',
    periodToSendInAPI: 'monthly',
  },
  {
    heading: 'Weekly Plan',
    subheading: '1 wk • USD$3.99',
    amount: '$3.99/ wk',
    isFreeTrialAvailable: '14-DAY FREE TRIAL',
    periodToSendInAPI: 'weekly',
  },
];

/**
 * PromoCodeSection component for handling promo code input
 */
const PromoCodeSection = ({
  promoCode,
  setPromoCode,
  promoApplied,
  setPromoApplied,
  promoError,
  setPromoError,
  isValidating,
  setIsValidating,
}) => {
  const [showPromoField, setShowPromoField] = useState(false);

  const handlePromoCodeChange = event => {
    setPromoCode(event.target.value);
    setPromoError('');
    setPromoApplied(false);
  };

  const validatePromoCode = async () => {
    if (!promoCode.trim()) return;

    setIsValidating(true);
    try {
      // Replace this with your actual API call
      // Example: const response = await paymentsApi.validatePromoCode(promoCode);

      // Simulate API call for now
      await new Promise(resolve => setTimeout(resolve, 1000));

      // Simulate the API response you mentioned
      if (promoCode.toLowerCase() === 'invalid' || promoCode.toLowerCase() === 'expired') {
        // Simulate the API error response format
        const error = new Error('API Error');
        error.response = {
          data: {
            detail: 'Invalid or expired promo code',
          },
        };
        throw error;
      }

      setPromoApplied(true);
      setPromoError('');
    } catch (error) {
      // Handle the specific API error response format
      if (error.response && error.response.data && error.response.data.detail) {
        setPromoError(error.response.data.detail);
      } else {
        setPromoError('Error validating promo code. Please try again.');
      }
      setPromoApplied(false);
    } finally {
      setIsValidating(false);
    }
  };

  const handleKeyPress = event => {
    if (event.key === 'Enter') {
      validatePromoCode();
    }
  };

  return (
    <Box sx={{mb: 3}}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          cursor: 'pointer',
          p: 2,
          backgroundColor: '#EAF9F9',
          borderRadius: '40px',
          mb: 1,
          maxWidth: '346px',
          minWidth: '100%',
          height: '48px',
          boxSizing: 'border-box',
        }}
        onClick={() => setShowPromoField(!showPromoField)}
      >
        <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
          <LocalOfferIcon sx={{color: '#72D8DA'}} />
          <Typography
            sx={{
              color: '#000',
              fontSize: '15px',
              fontWeight: 600,
            }}
          >
            Have a promo code?
          </Typography>
        </Box>
        <IconButton size="small">{showPromoField ? <ExpandLessIcon /> : <ExpandMoreIcon />}</IconButton>
      </Box>

      <Collapse in={showPromoField}>
        <Box
          sx={{
            p: 2,
            backgroundColor: '#F1FBFB',
            borderRadius: '10px',
            border: '2px solid rgba(114, 216, 218, 0.30)',
            mb: 2,
          }}
        >
          <Box sx={{display: 'flex', gap: 1, mb: 2}}>
            <TextField
              fullWidth
              size="small"
              placeholder="Enter promo code"
              value={promoCode}
              onChange={handlePromoCodeChange}
              onKeyPress={handleKeyPress}
              error={!!promoError}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '20px',
                },
              }}
            />
            <Button
              variant="outlined"
              onClick={validatePromoCode}
              disabled={!promoCode.trim() || isValidating}
              sx={{
                minWidth: 'fit-content',
                px: 3,
                borderRadius: '20px',
                borderColor: '#72D8DA',
                color: '#72D8DA',
                '&:hover': {
                  borderColor: '#5fb8b8',
                  backgroundColor: 'rgba(114, 216, 218, 0.04)',
                },
              }}
            >
              {isValidating ? <CircularProgress size={20} sx={{color: '#72D8DA'}} /> : 'Apply'}
            </Button>
          </Box>

          {promoError && (
            <Alert severity="error" sx={{mb: 1, borderRadius: '10px'}}>
              {promoError}
            </Alert>
          )}

          {promoApplied && (
            <Alert severity="success" sx={{mb: 1, borderRadius: '10px'}}>
              <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                <Typography variant="body2">Promo code applied successfully!</Typography>
                <Chip label={promoCode} size="small" color="success" variant="outlined" />
              </Box>
            </Alert>
          )}
        </Box>
      </Collapse>
    </Box>
  );
};

const PrisingScreen = () => {
  const navigate = useNavigate();
  const userAPI = useUserApi();
  const [selectedCard, setSelectedCard] = useState(0);
  const [planPeriod, setPalnPeriod] = useState('yearly');
  const [enableFreeTrial, setEnableFreeTrial] = useState(false);
  const [showNormalPricingScreen, setShowNormalPricingScreen] = useState(false);
  const location = useLocation(); // Access location object to get the state
  const [loading, isLoading] = useState(false);
  const [responseReturned, setResponseReturned] = useState(false);
  const paymentsApi = useCheckoutPaymentsApi();
  const {flowComingFromResultPage, flowComingFromRegitrstionPage} = location.state || {};
  const [message, setMessage] = useState(undefined);

  // Promo code states
  const [promoCode, setPromoCode] = useState('');
  const [promoApplied, setPromoApplied] = useState(false);
  const [promoError, setPromoError] = useState('');
  const [isValidating, setIsValidating] = useState(false);

  useEffect(() => {
    if (flowComingFromResultPage) {
      setShowNormalPricingScreen(true);
    } else if (flowComingFromRegitrstionPage) {
      setShowNormalPricingScreen(false);
    }
  }, [flowComingFromResultPage, flowComingFromRegitrstionPage]);

  const handleCardClick = (index, period) => {
    console.log(period);
    setPalnPeriod(period);
    setSelectedCard(index === selectedCard ? null : index); // Toggle selection
  };

  //handle payment
  const handleCheckout = async (period, appliedPromoCode = '') => {
    try {
      // Set UI states to show loading and wait for response
      isLoading(true);

      // Use the promo code if it's applied, otherwise use empty string
      const finalPromoCode = promoApplied ? appliedPromoCode : '';
      // get checkout link from API
      const checkoutLinkReq = await paymentsApi.postCheckout(period, finalPromoCode);

      if (checkoutLinkReq.status === 200) {
        // Redirect the user to the checkout page
        const link = checkoutLinkReq.data.redirect_url;
        window.location.href = link;
      } else {
        isLoading(false);
        // Handle errors if the request was not successful
        throw new Error('Failed to get checkout link');
      }
    } catch (error) {
      console.error('Error during checkout:', error);
      // Update UI with error message
      setResponseReturned(true);
      isLoading(false);

      // Handle specific API error responses
      if (error.response && error.response.data && error.response.data.detail) {
        setMessage(error.response.data.detail);
      } else {
        setMessage('An error occurred. Please try again later.');
      }
    }
  };

  const post14DaysTrialStartAPI = async () => {
    try {
      isLoading(true);
      // get checkout link from API
      const postStartTrial = await userAPI.postStartUserTrial();
      if (postStartTrial.status === 200) {
        isLoading(false);
        navigate('/demographics');
      }
    } catch (error) {
      console.error('Error during checkout:', error);
      isLoading(false);
      setMessage('An error occurred. Please try again later.');
    }
  };

  const handleMainBtnClick = period => {
    if (showNormalPricingScreen && !enableFreeTrial) {
      handleCheckout(period, promoCode);
    } else if (!showNormalPricingScreen && enableFreeTrial) {
      post14DaysTrialStartAPI();
    } else if (!showNormalPricingScreen && !enableFreeTrial) {
      handleCheckout(period, promoCode);
    }
  };

  // Handler to toggle the free trial state
  const handleFreeTrialToggle = event => {
    setEnableFreeTrial(event.target.checked); // Set the state based on the switch status
  };

  const handleMaybeLaterBtn = () => {
    navigate('/demographics');
  };

  return (
    <>
      {loading ? (
        <AnimationLoadingScreenWithProgressBar title="Loading..." />
      ) : (
        <>
          <AimdFlowQuestionBase
            IsShowBackIcon={showNormalPricingScreen ? false : true}
            isShowMenuIcon="true"
            isShowStepper={false}
            isBottomLogoVisible={false}
          >
            <Box>
              <Stack sx={{textAlign: 'center', width: '100%', alignItems: 'center', marginBottom: '14px'}}>
                <img src={mainLogo} width="100px" height="auto" />
              </Stack>
              <Stack>
                <Typography
                  sx={{
                    color: '#000',
                    textAlign: 'center',
                    fontSize: '28px',
                    fontWeight: 700,
                    marginBottom: '30px',
                  }}
                >
                  Choose Your Plan
                </Typography>
              </Stack>

              {!showNormalPricingScreen && (
                <Stack
                  sx={{
                    maxWidth: '346px',
                    minWidth: '100%',
                    padding: '4px 16px',
                    boxSizing: 'border-box',
                    height: '48px',
                    borderRadius: '40px',
                    background: '#EAF9F9',
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '16px',
                  }}
                >
                  <Typography
                    sx={{
                      color: '#000',
                      fontSize: '15px',
                      fontWeight: 600,
                    }}
                  >
                    Not sure yet? Enable free trial.{' '}
                  </Typography>

                  <Switch
                    focusVisibleClassName=".Mui-focusVisible"
                    sx={{
                      borderRadius: '30px',
                      '& .MuiSwitch-thumb': {
                        width: 22,
                        height: 22,
                        borderRadius: '50%',
                        backgroundColor: '#fff',
                      },
                    }}
                    checked={enableFreeTrial}
                    onChange={handleFreeTrialToggle}
                    disableRipple
                  />
                </Stack>
              )}

              {/* Promo Code Section */}
              <PromoCodeSection
                promoCode={promoCode}
                setPromoCode={setPromoCode}
                promoApplied={promoApplied}
                setPromoApplied={setPromoApplied}
                promoError={promoError}
                setPromoError={setPromoError}
                isValidating={isValidating}
                setIsValidating={setIsValidating}
              />

              <Stack>
                {pricingScreendata &&
                  pricingScreendata.map((data, index) => (
                    <Box
                      onClick={() => handleCardClick(index, data.periodToSendInAPI)}
                      key={index}
                      sx={{
                        maHeight: '111px',
                        maxWidth: '347px',
                        minWidth: '100%',
                        marginBottom: '12px',
                        backgroundColor: selectedCard === index ? '#F1FBFB' : '#FFF',
                        // border: "3px solid #72D8DA",
                        border: selectedCard === index ? '5px solid #72D8DA' : '4px solid rgba(114, 216, 218, 0.50)',
                        borderRadius: '10px',
                      }}
                    >
                      {!showNormalPricingScreen && selectedCard === index && enableFreeTrial && (
                        <Typography
                          sx={{
                            height: '24px',
                            backgroundColor: '#72D8DA',
                            fontSize: '15px',
                            color: '#fff',
                            fontWeight: 600,
                            textAlign: 'center',
                          }}
                        >
                          {data?.isFreeTrialAvailable}
                        </Typography>
                      )}
                      <Box
                        sx={{
                          display: 'flex',
                          boxSizing: 'border-box',
                          flexDirection: 'row',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          padding: '10px',
                        }}
                      >
                        <Box>
                          <Typography
                            sx={{
                              fontSize: '23px',
                              fontWeight: 800,
                              color: '#000',
                            }}
                          >
                            {data.heading}
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: '16px',
                              fontWeight: 600,
                              color: '#000',
                            }}
                          >
                            {data.subheading}
                          </Typography>
                          {/* Show promo applied indicator */}
                          {promoApplied && selectedCard === index && (
                            <Chip
                              label="Discount Applied"
                              size="small"
                              color="success"
                              sx={{mt: 1, fontSize: '10px'}}
                            />
                          )}
                        </Box>
                        <Box
                          sx={{
                            display: 'flex',
                            flexDirection: 'row',
                            alignItems: 'center',
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: '18px',
                              fontWeight: 600,
                              color: '#000',
                              paddingRight: '6px',
                            }}
                          >
                            {data.amount}
                          </Typography>
                          {selectedCard === index ? (
                            <CheckCircleIcon
                              sx={{
                                color: '#72D8DA',
                              }}
                            />
                          ) : (
                            <RadioButtonUncheckedIcon
                              sx={{
                                color: '#72D8DA',
                              }}
                            />
                          )}
                        </Box>
                      </Box>
                    </Box>
                  ))}
              </Stack>

              <Button
                onClick={() => handleMainBtnClick(planPeriod)}
                sx={{
                  borderRadius: '40px', // Rounded corners
                  background: '#72D8DA', // Background color
                  boxShadow: '0px 35px 35px 0px rgba(0, 0, 0, 0.09), 0px 9px 19px 0px rgba(0, 0, 0, 0.10)', // Box shadow
                  width: '320px', // Width of the button
                  height: '65px', // Height of the button
                  flexShrink: 0, // Prevent shrinking
                  color: 'white',
                  fontSize: '20px',
                  fontWeight: 600,
                  '&:hover': {
                    background: '#5fb8b8', // Adjust background on hover for better UI feedback
                  },
                  marginTop: {lg: '50px', md: '40px', sm: '20px', xs: '20px'},
                }}
              >
                {showNormalPricingScreen ? 'Continue' : enableFreeTrial ? 'Start 14-Day Free Trial' : 'Continue'}
              </Button>
              <Typography
                onClick={handleMaybeLaterBtn}
                sx={{
                  color: '#A1A0A0',
                  textAlign: 'center',
                  fontSize: '17px',
                  fontWeight: 600,
                  marginTop: '10px',
                  cursor: 'pointer',
                }}
              >
                Maybe later
              </Typography>
            </Box>
          </AimdFlowQuestionBase>

          <Snackbar
            autoHideDuration={2000}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'center',
            }}
            open={message !== undefined}
            onClose={() => {
              setMessage(undefined);
            }}
          >
            <Alert
              severity="error"
              sx={{
                mt: 0.5,
                mb: 6,
                alignSelf: 'center',
                width: '90%', //even though is not 100% it looks like it is
                textAlign: 'center',
              }}
            >
              {message}
            </Alert>
          </Snackbar>
        </>
      )}
    </>
  );
};

export default PrisingScreen;
